import type { GuideArticle } from "./types";

/**
 * Seed editorial articles for the Guide Hub (Task #4409).
 *
 * Adding a new article: append a `GuideArticle` here, point `category` at an
 * existing `GUIDE_CATEGORIES` slug, give it a unique `slug` (must NOT be
 * "category" and must not collide with any existing top-level route), then run
 * the prerender content build. The api-server mirror + sync test enforce the
 * sitemap stays in step.
 *
 * Bodies use the shared `Block[]` schema. Inline links use markdown syntax:
 * `[label](/href)`; the renderer routes internal hrefs (see
 * `INTERNAL_PREFIXES` in LegalContent) through wouter so they stay SPA-native.
 */
export const GUIDE_ARTICLES: GuideArticle[] = [
  {
    slug: "how-to-hire-a-classic-car",
    category: "hiring-guides",
    title: "How to Hire a Classic Car: A Complete Beginner's Guide",
    excerpt:
      "New to classic car hire? Here's exactly how it works on {brandName}, from finding the right car to picking up the keys.",
    metaTitle: "How to Hire a Classic Car: A Beginner's Guide | {brandName}",
    metaDescription:
      "A step-by-step beginner's guide to hiring a classic car: how booking works, what you need, how cover and deposits work, and how to choose the right car.",
    hero: {
      image: "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1920&q=80",
      alt: "Person holding the keys to a classic convertible",
    },
    author: "The {brandName} Team",
    readingMinutes: 7,
    body: [
      { p: "Hiring a classic car is not the same as renting a hatchback at the airport. You're borrowing something rare, often hand-built, and almost always owned by an enthusiast who cares about it deeply. That changes how the process works. Once you understand it, booking your first classic is genuinely simple." },
      { h3: "1. Decide what the car is for" },
      { p: "The right classic depends entirely on the occasion. A wedding wants elegance and presence; a weekend in the hills wants an open top and a willing engine; a film shoot wants period accuracy. Start by browsing our [hire guides by occasion](/hire) to narrow the field before you fall in love with the first photo you see." },
      { p: "If you already know the model you want, our [classic car model guides](/models) cover what each one is actually like to live with for a day, including the charm and the quirks." },
      { h3: "2. Search and compare" },
      { p: "Every car on {brandName} is listed by its owner, so no two are identical. Use [Rent a car](/search) to filter by location, dates, make, and model. Read the listing carefully: check the mileage limits, whether it is a self-drive or Chauffeur rental, and what the owner expects at handover." },
      { ul: [
        "Self-drive puts you behind the wheel: ideal for road trips and experiences.",
        "Chauffeur hire includes a driver, and is the usual choice for weddings and events.",
        "Not sure which suits you? Read [self-drive vs Chauffeur](/guides/self-drive-vs-chauffeured).",
      ] },
      { h3: "3. Request your dates" },
      { p: "Send a booking request with your dates and a short note about the occasion. Owners love context. Telling them it's your parents' golden anniversary often gets you a warmer welcome and the odd extra touch. The owner confirms, and your payment is only taken once they accept." },
      { h3: "4. Insurance, deposits and paperwork" },
      { p: "For self-drive hires of cars located in the United States, insurance is included through {brandName} and selected at checkout. Everywhere else, the owner arranges the cover for the hire; check the listing for its terms. A refundable security deposit is held against damage, as is standard practice for valuable cars. You'll usually need a full licence held for a minimum period and a second form of ID. Check the listing for the exact requirements, and see our [Help Centre](/help) for the fine print." },
      { h3: "5. Collection and the drive" },
      { p: "On the day, the owner walks you around the car, points out the controls (classics can be charmingly idiosyncratic), and notes the fuel and mileage. Take your time, ask questions, and enjoy it; that's the whole point." },
      { strong: "Ready to start? [Rent a car](/search) and find a car for your dates." },
    ],
    faqs: [
      { q: "Do I need special insurance to hire a classic car?", a: "No. You don't arrange your own policy. For U.S. self-drive hires, insurance is included through {brandName}; elsewhere the owner arranges the cover for the hire. You'll typically need a full driving licence held for a minimum period; the exact terms are shown on each listing." },
      { q: "Is there a deposit?", a: "Yes. A refundable security deposit is held against damage and returned after the car comes back in the same condition. The amount varies by car and is shown before you book." },
      { q: "Can I drive a classic car myself?", a: "Often, yes. Many listings are self-drive. Some owners only offer chauffeured hire for their most valuable cars. Each listing states which option applies." },
    ],
    commercialLinks: [
      { label: "Rent a car", href: "/search" },
      { label: "Browse hire guides by occasion", href: "/hire" },
      { label: "Explore classic car model guides", href: "/models" },
    ],
    related: ["cost-of-hiring-a-classic-car", "self-drive-vs-chauffeured"],
    publishedAt: "2026-01-15",
    updatedAt: "2026-06-01",
  },
  {
    slug: "cost-of-hiring-a-classic-car",
    category: "hiring-guides",
    title: "What Does It Cost to Hire a Classic Car?",
    excerpt:
      "A clear breakdown of classic car hire pricing: what drives the daily rate, what's included, and how to get the best value.",
    metaTitle: "How Much Does Classic Car Hire Cost? Price Guide | {brandName}",
    metaDescription:
      "What classic car hire really costs: how daily rates are set, what's included, how deposits and cover work, and practical tips to get the best value.",
    hero: {
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80",
      alt: "Classic car detail with chrome and badge",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "\"How much does it cost?\" is the first question almost everyone asks. The honest answer is that it depends, but the maths is straightforward once you know what you're paying for." },
      { h3: "What drives the daily rate" },
      { ul: [
        "Rarity and desirability: a concours E-Type commands more than a tidy MGB.",
        "Whether it's self-drive or chauffeured: a driver's time is part of the price.",
        "Season and demand: wedding season and summer weekends are busiest.",
        "Duration: many owners offer better per-day rates on multi-day hires.",
      ] },
      { h3: "What's usually included" },
      { p: "Most listings include a set mileage allowance in the headline price, and cover for the hire is arranged by the owner or included through {brandName} for U.S. self-drive trips. Always read the listing: some include generous mileage, others keep it tight with a per-mile charge beyond the limit. For self-drive trips, factor in fuel; classics are not famous for economy." },
      { h3: "Deposits are separate" },
      { p: "A refundable security deposit is held against damage and returned after the car comes back as it left. It's not a cost in the usual sense, but you'll need the headroom on your card." },
      { h3: "Getting the best value" },
      { ul: [
        "Be flexible on dates; midweek and shoulder-season are cheaper.",
        "Book multi-day where it makes sense for a better per-day rate.",
        "Compare similar cars in your area using [search](/search).",
        "Consider a less obvious model. Our [model guides](/models) highlight characterful cars that cost less than the icons.",
      ] },
      { p: "Planning a specific occasion changes the calculus too. A wedding is a few hours with a chauffeur; a road trip is several self-drive days. See our [hire guides](/hire) for occasion-by-occasion expectations." },
      { strong: "Browse live listings to see real prices for your dates and location." },
    ],
    faqs: [
      { q: "Is fuel included in classic car hire?", a: "For self-drive hires, fuel is normally your responsibility: you return the car with the same level you collected it. Chauffeured hires usually include fuel in the quote." },
      { q: "Why do prices vary so much between similar cars?", a: "Condition, rarity, mileage allowance, and whether a driver is included all move the price. Each car is owned by an individual who sets their own rate." },
      { q: "Do longer hires cost less per day?", a: "Frequently, yes. Many owners offer a lower daily rate for multi-day bookings; it's always worth asking." },
    ],
    commercialLinks: [
      { label: "Compare prices in search", href: "/search" },
      { label: "Browse model guides", href: "/models" },
      { label: "See hire guides by occasion", href: "/hire" },
    ],
    related: ["how-to-hire-a-classic-car", "self-drive-vs-chauffeured"],
    publishedAt: "2026-01-22",
    updatedAt: "2026-06-01",
  },
  {
    slug: "self-drive-vs-chauffeured",
    category: "hiring-guides",
    title: "Self-Drive vs Chauffeured Classic Car Hire: Which Is Right for You?",
    excerpt:
      "The two ways to hire a classic: behind the wheel yourself, or sitting back with a driver. Here's how to choose.",
    metaTitle: "Self-Drive vs Chauffeured Classic Car Hire | {brandName}",
    metaDescription:
      "Self-drive or chauffeured classic car hire? Compare the experience, cost, and best occasions for each so you choose the right option.",
    hero: {
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80",
      alt: "Driver at the wheel of a classic car",
    },
    author: "The {brandName} Team",
    readingMinutes: 5,
    body: [
      { p: "Every classic car hire comes down to one early decision: do you want to drive, or be driven? Both are wonderful. They just suit very different occasions." },
      { h3: "Self-drive: the car is yours for the day" },
      { p: "There's nothing quite like working a manual gearbox on an empty B-road in a car older than you are. Self-drive is the natural choice for road trips, driving experiences, and anyone who wants the full tactile experience of a classic." },
      { ul: [
        "Total freedom over route and pace.",
        "The authentic driving experience: the steering, the sound, the engagement.",
        "You'll need a qualifying licence and to meet the owner's age and experience terms.",
      ] },
      { h3: "Chauffeur: sit back and arrive in style" },
      { p: "For weddings, milestone celebrations, and corporate events, a professional driver is usually the right call. You get the entrance and the photographs without worrying about parking a priceless car or grinding an unfamiliar gearbox." },
      { ul: [
        "No licence or experience requirements for you.",
        "A polished, stress-free experience on the day.",
        "Ideal when the car is part of an event rather than the activity itself.",
      ] },
      { h3: "A quick way to decide" },
      { p: "If the drive is the point, go self-drive. If the arrival is the point, choose Chauffeur. For a deeper side-by-side, see our [comparison guides](/compare). Planning a wedding specifically? Start with our [classic wedding car guide](/guides/classic-wedding-car-hire-guide)." },
      { strong: "Filter for self-drive or Chauffeur rentals directly in search." },
    ],
    faqs: [
      { q: "Is chauffeured hire more expensive?", a: "Usually, because a professional driver's time is included. But for short, high-stakes occasions like weddings it's often the better value, and far less stressful." },
      { q: "Can anyone self-drive a classic car?", a: "You'll need a full licence held for a minimum period and to meet the owner's age/experience terms. These are listed on each car." },
      { q: "Which is better for a wedding?", a: "Chauffeured hire is the usual choice for weddings. It's relaxed, looks immaculate, and you never have to think about parking." },
    ],
    commercialLinks: [
      { label: "Browse comparison guides", href: "/compare" },
      { label: "Search classic cars", href: "/search" },
      { label: "Wedding car hire guides", href: "/hire" },
    ],
    related: ["how-to-hire-a-classic-car", "classic-wedding-car-hire-guide"],
    publishedAt: "2026-02-05",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-wedding-car-hire-guide",
    category: "occasions",
    title: "The Complete Guide to Classic Wedding Car Hire",
    excerpt:
      "Choosing, booking, and styling the perfect classic wedding car: timings, etiquette, and the models that photograph beautifully.",
    metaTitle: "Classic Wedding Car Hire: The Complete Guide | {brandName}",
    metaDescription:
      "Plan your classic wedding car: how far ahead to book, chauffeured vs self-drive, the most photogenic models, and timing your big day around the car.",
    hero: {
      image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80",
      alt: "Elegant vintage wedding car with ribbon outside a church",
    },
    author: "The {brandName} Team",
    readingMinutes: 8,
    body: [
      { p: "The wedding car is one of the few props that appears in your photographs, your arrival, and your getaway. A great classic ties the whole day together. Getting it right is mostly about planning early and choosing a car that matches your style." },
      { h3: "Book early, earlier than you think" },
      { p: "Wedding season is the busiest time for classic hire. The most sought-after cars get reserved twelve months ahead, so once your venue and date are set, the car should be near the top of your list. Browse what's available for your area in [search](/search) and send a request as soon as you can." },
      { h3: "Chauffeured is the norm" },
      { p: "Almost all wedding hires are chauffeured. You arrive relaxed, the car looks immaculate, and nobody has to learn an unfamiliar gearbox in formalwear. If you're weighing it up, our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) lays out the trade-offs." },
      { h3: "Choosing a car that photographs well" },
      { p: "Different eras set very different tones:" },
      { ul: [
        "Pre-war and vintage cars for timeless, romantic elegance.",
        "1950s and 60s British saloons for understated class.",
        "Open-top roadsters for relaxed summer weddings.",
        "Statement luxury for a grand, formal entrance.",
      ] },
      { p: "Our [classic car model guides](/models) describe how each model looks and feels, which helps you picture it against your colour scheme and venue." },
      { h3: "Timing the day around the car" },
      { p: "Build in buffer time. Classics travel at a stately pace and benefit from an unhurried schedule. Agree the route and timings with the owner in advance, and think about whether you want the car for the departure too. A vintage getaway is a lovely final photograph." },
      { h3: "Styling and etiquette" },
      { p: "Most owners provide traditional ribbon; ask before adding anything else, as some materials can mark delicate paint. Confirm how many passengers the car comfortably seats, since early classics are smaller inside than they look." },
      { strong: "Find a wedding-ready classic in your area and request your date." },
    ],
    faqs: [
      { q: "How far in advance should I book a wedding car?", a: "Aim for nine to twelve months for popular cars in peak season. Once your date and venue are confirmed, book the car early." },
      { q: "Should a wedding car be self-drive or chauffeured?", a: "Chauffeured is the standard choice. It's relaxed, looks polished, and removes any worry about driving and parking a valuable car on the day." },
      { q: "Can the car take us to the reception too?", a: "Often yes. Many couples book the car for both the arrival and the getaway. Confirm the timings and any additional cost with the owner when you book." },
    ],
    commercialLinks: [
      { label: "Search classic wedding cars", href: "/search" },
      // Task #7156 — link mesh: the worldwide wedding use-case guide exists in
      // every market and itself links each market's curated wedding hub.
      { label: "Wedding car hire: how it works & costs", href: "/hire/wedding-car-hire" },
      { label: "Browse photogenic classic models", href: "/models" },
    ],
    related: ["self-drive-vs-chauffeured", "best-classic-cars-for-special-occasions"],
    publishedAt: "2026-02-18",
    updatedAt: "2026-06-01",
  },
  {
    slug: "best-classic-cars-for-special-occasions",
    category: "occasions",
    title: "The Best Classic Cars for Special Occasions",
    excerpt:
      "Anniversaries, big birthdays, proposals and gifts: the classic cars that turn a milestone into a memory.",
    metaTitle: "Best Classic Cars for Special Occasions & Gifts | {brandName}",
    metaDescription:
      "The best classic cars to hire for anniversaries, milestone birthdays, proposals, and once-in-a-lifetime gifts, and how to match the car to the moment.",
    hero: {
      image: "https://images.unsplash.com/photo-1502136969935-8d8eef54d77b?w=1920&q=80",
      alt: "Couple beside a classic car at sunset",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "Some moments are too good for an ordinary car. A landmark anniversary, a surprise birthday drive, or a proposal with the roof down; the right classic turns the occasion into the story you tell for years." },
      { h3: "Matching the car to the milestone" },
      { ul: [
        "Anniversaries: a car from the year you met or married is unbeatably sentimental.",
        "Milestone birthdays: go bold with a sports car or a head-turning convertible.",
        "Proposals: an open-top classic on a scenic route sets an unforgettable scene.",
        "Gifts: a self-drive experience day is a present they'll never forget.",
      ] },
      { h3: "Sentimental over flashy" },
      { p: "The most memorable choices are often personal rather than expensive. Tracking down the exact model someone owned in their twenties means more than the priciest car in the listings. Our [model guides](/models) make it easy to find a specific make and model and read what it's like today." },
      { h3: "Make it an experience" },
      { p: "Pair the car with a great drive. Our [destination and road-trip guides](/guides/category/destinations) suggest routes worth building a day around, and the [hire guides](/hire) cover gifting and experience-day bookings in more detail." },
      { p: "For weddings specifically, our [classic wedding car guide](/guides/classic-wedding-car-hire-guide) goes deeper on timing and etiquette." },
      { strong: "Find the perfect car for the occasion and surprise someone." },
    ],
    faqs: [
      { q: "What's a good classic car gift idea?", a: "A self-drive experience day in a car the recipient has always wanted, or the exact model they once owned, is consistently the most memorable gift." },
      { q: "Can I hire a car from a specific year?", a: "Often, yes. Use the model guides and search to find cars from a particular era, perfect for anniversaries tied to a meaningful year." },
      { q: "Is a classic car suitable for a proposal?", a: "Wonderfully so. An open-top classic on a quiet, scenic route makes for an intimate and unforgettable setting." },
    ],
    commercialLinks: [
      { label: "Search by make and model", href: "/search" },
      { label: "Browse classic model guides", href: "/models" },
      { label: "Occasion & gift hire guides", href: "/hire" },
    ],
    related: ["classic-wedding-car-hire-guide", "classic-car-road-trips-europe"],
    publishedAt: "2026-03-04",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-car-road-trips-europe",
    category: "destinations",
    title: "Classic Car Road Trips: Europe's Greatest Driving Routes",
    excerpt:
      "From Alpine passes to coastal corniches, the routes that were made for an open-top classic, and the cars to drive them in.",
    metaTitle: "Best European Classic Car Road Trips & Driving Routes | {brandName}",
    metaDescription:
      "Europe's greatest driving roads for a classic car road trip, from Alpine passes and coastal routes to Tuscan hills, with planning tips and the cars to do them in.",
    hero: {
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80",
      alt: "Classic car on a scenic European mountain pass",
    },
    author: "The {brandName} Team",
    readingMinutes: 9,
    body: [
      { p: "A classic car and a great road are made for each other. No screens, no distractions, just an engine you can hear, a gearbox you have to think about, and scenery that earns every mile. Europe is full of routes that feel designed for exactly this." },
      { h3: "The routes worth planning a trip around" },
      { ul: [
        { text: "Coastal corniches: the Côte d'Azur is the spiritual home of open-top motoring. See our [Côte d'Azur city guide](/cities) for where to base yourself." },
        "Tuscan hills: cypress-lined ridge roads and hilltop towns at a gentle, sun-warmed pace.",
        "Alpine passes: dramatic, demanding, and unforgettable in a willing classic.",
        "The British countryside: the Cotswolds and the Highlands reward an unhurried roof-down day.",
      ] },
      { h3: "Choosing the right car for the road" },
      { p: "Match the car to the terrain. A nimble roadster shines on tight coastal roads; a comfortable grand tourer eats long Alpine days; a characterful small classic is pure joy on quiet country lanes. Our [model guides](/models) describe how each car copes with real-world driving, not just how it looks." },
      { p: "All of these are self-drive trips by nature; the driving is the holiday. If you're new to it, read [how to hire a classic car](/guides/how-to-hire-a-classic-car) first." },
      { h3: "Planning practicalities" },
      { ul: [
        "Confirm mileage allowances up front; road trips rack up miles fast.",
        "Build in slack: classics prefer a relaxed schedule and the odd cooling stop.",
        "Check cross-border terms with the owner if your route leaves the country.",
        "Pack light: boot space in a classic is often more decorative than practical.",
      ] },
      { p: "Browse classic-friendly destinations in our [city and region guides](/cities), then find a car near your starting point in [search](/search)." },
      { strong: "Pick your route, choose your car, and plan the drive of a lifetime." },
    ],
    faqs: [
      { q: "Can I take a hired classic car abroad or across borders?", a: "Sometimes. It depends entirely on the owner and the insurance terms. Always agree any cross-border travel with the owner before you book." },
      { q: "What's the best type of classic for a road trip?", a: "For long days, a comfortable grand tourer; for twisty coastal and mountain roads, a lighter roadster. The model guides describe each car's road manners." },
      { q: "How do mileage limits work on a road trip?", a: "Many listings include a daily mileage allowance with a per-mile charge beyond it. For road trips, confirm a generous allowance with the owner in advance." },
    ],
    commercialLinks: [
      { label: "Browse city & region guides", href: "/cities" },
      { label: "Find a car near your route", href: "/search" },
      { label: "Explore touring-ready models", href: "/models" },
    ],
    related: ["how-to-hire-a-classic-car", "best-classic-cars-for-special-occasions"],
    publishedAt: "2026-03-20",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-car-hire-insurance-explained",
    category: "hiring-guides",
    title: "Classic Car Hire Insurance and Deposits Explained",
    excerpt:
      "What cover comes with a classic car hire, how the security deposit works, and the licence and age terms you'll usually need to meet.",
    metaTitle: "Classic Car Hire Insurance & Deposits Explained | {brandName}",
    metaDescription:
      "How insurance and security deposits work when you hire a classic car: what's covered, what the deposit protects, and the licence and age requirements to expect.",
    hero: {
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80",
      alt: "Close-up of a classic car's chrome bumper and badge",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "Insurance is the part of classic car hire people worry about most, but it's actually the most straightforward once you know how it fits together. Here's what's covered, what the deposit is for, and what you'll need to qualify." },
      { h3: "Who arranges the cover" },
      { p: "You don't need to organise your own policy. For self-drive hires of cars located in the United States, insurance is provided through {brandName} and selected at checkout. Everywhere else, the owner arranges the cover for the hire, so the terms vary by listing. What matters is meeting the terms attached to it; that's where the licence and age requirements come in. If you're new to the whole process, start with [how to hire a classic car](/guides/how-to-hire-a-classic-car)." },
      { h3: "What the security deposit protects" },
      { p: "Separately from insurance, a refundable security deposit is held against damage. It's not a charge in the usual sense; it's headroom on your card that's released after the car comes back as it left. The amount varies by car and is shown before you book." },
      { ul: [
        "The deposit covers minor damage, excess, and the return condition of the car.",
        "It's refunded once the owner has checked the car back in.",
        "Keep the headroom free on your card for the duration of the hire.",
      ] },
      { h3: "Licence and age requirements" },
      { p: "For self-drive hires you'll usually need a full licence held for a minimum period, and to meet the owner's age and driving-experience terms. Some owners set a higher minimum age for their most valuable cars. Every listing states its own requirements, so read them before you request dates. Chauffeured hire removes this entirely, since a professional driver is at the wheel; see [self-drive vs chauffeured](/guides/self-drive-vs-chauffeured) for the trade-offs." },
      { h3: "Where to check the detail" },
      { p: "The exact insurance terms, deposit amount, and driver requirements live on each individual listing, because every car is owned by a different person. For general questions, our [Help Centre](/help) covers the fine print, and you can always message an owner before booking." },
      { strong: "Browse listings to see the deposit and driver terms for cars in your area." },
    ],
    faqs: [
      { q: "Do I need to arrange my own insurance to hire a classic car?", a: "No. For U.S. self-drive hires, insurance is provided through {brandName}; for all other hires the owner arranges the cover. You just need to meet the licence, age, and experience terms shown on the listing." },
      { q: "Is the security deposit a fee?", a: "No. It's a refundable hold against damage, released after the car is returned and checked in. Keep that amount free on your card for the hire." },
      { q: "What licence do I need to self-drive a classic?", a: "Typically a full licence held for a minimum period, plus meeting the owner's age and experience terms. The exact requirement is on each listing." },
    ],
    commercialLinks: [
      { label: "Search classic cars for hire", href: "/search" },
      { label: "Read the beginner's hire guide", href: "/guides/how-to-hire-a-classic-car" },
      { label: "Visit the Help Centre", href: "/help" },
    ],
    related: ["how-to-hire-a-classic-car", "cost-of-hiring-a-classic-car", "what-you-need-to-hire-a-classic-car"],
    publishedAt: "2026-04-02",
    updatedAt: "2026-06-01",
  },
  {
    slug: "what-you-need-to-hire-a-classic-car",
    category: "hiring-guides",
    title: "What You Need to Hire a Classic Car: The Checklist",
    excerpt:
      "Licence, ID, age, deposit headroom and the right expectations: a simple checklist so your first classic car hire goes smoothly.",
    metaTitle: "What You Need to Hire a Classic Car: Checklist | {brandName}",
    metaDescription:
      "A practical checklist for hiring a classic car: the documents, licence and age requirements, deposit headroom, and what to confirm with the owner before you book.",
    hero: {
      image: "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1920&q=80",
      alt: "Hand holding the keys to a classic convertible",
    },
    author: "The {brandName} Team",
    readingMinutes: 5,
    body: [
      { p: "Hiring a classic is simple once you've got the essentials in order. Run through this checklist before you send a booking request and the rest of the process takes care of itself." },
      { h3: "Before you book" },
      { ul: [
        "A full driving licence held for the minimum period the owner asks for (self-drive only).",
        "A second form of ID; many owners ask for one at handover.",
        "Enough headroom on your card for the refundable security deposit.",
        "Your dates and a short note on the occasion; context goes a long way with owners.",
      ] },
      { h3: "Decide self-drive or chauffeured first" },
      { p: "This shapes everything else. Self-drive means meeting the licence and age terms; chauffeured means you can leave the driving to a professional. If you're unsure, our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) lays it out clearly." },
      { h3: "Confirm the practical details with the owner" },
      { ul: [
        "Mileage allowance and any per-mile charge beyond it.",
        "Collection and return times and location.",
        "Fuel policy: for self-drive you'll usually return it as you found it.",
        "Any quirks of the car the owner wants to walk you through.",
      ] },
      { h3: "Understand the money side" },
      { p: "Payment is only taken once the owner accepts your request, and the deposit is held separately and refunded after a clean return. For the full picture of pricing and cover, read [what classic car hire costs](/guides/cost-of-hiring-a-classic-car) and our [insurance and deposits guide](/guides/classic-car-hire-insurance-explained)." },
      { strong: "Got everything ready? Find a car for your dates and send your request." },
    ],
    faqs: [
      { q: "What documents do I need to hire a classic car?", a: "For self-drive, a full driving licence held for the owner's minimum period plus usually a second form of ID. Chauffeured hire needs no licence from you." },
      { q: "How old do I need to be to hire a classic car?", a: "It depends on the owner and the car. Some set a higher minimum age for valuable models. Each listing states its own age and experience terms." },
      { q: "When is payment taken?", a: "Only after the owner accepts your booking request. A refundable security deposit is held separately and returned after a clean handover." },
    ],
    commercialLinks: [
      { label: "Search classic cars for hire", href: "/search" },
      { label: "Compare self-drive and chauffeured", href: "/guides/self-drive-vs-chauffeured" },
      { label: "See how pricing works", href: "/guides/cost-of-hiring-a-classic-car" },
    ],
    related: ["how-to-hire-a-classic-car", "classic-car-hire-insurance-explained", "self-drive-vs-chauffeured"],
    publishedAt: "2026-04-09",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-cars-for-film-and-photoshoots",
    category: "occasions",
    title: "Hiring Classic Cars for Film, TV and Photoshoots",
    excerpt:
      "Picture cars done right: how to source period-accurate classics for film, TV and photoshoots, and what owners need to know in return.",
    metaTitle: "Classic Cars for Film, TV & Photoshoot Hire | {brandName}",
    metaDescription:
      "How to hire period-accurate classic cars for film, TV, and photoshoots: choosing the right era, planning the shoot day, and what to agree with the owner.",
    hero: {
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80",
      alt: "Classic car styled on location for a photoshoot",
    },
    author: "The {brandName} Team",
    readingMinutes: 7,
    body: [
      { p: "A picture car can make or break a period scene. Get the era, the colour, and the condition right and the whole frame sings; get it wrong and even a casual viewer feels something is off. Sourcing the right classic for a shoot is part research, part planning. Here's how to do both." },
      { h3: "Nail the period and the look" },
      { p: "Start with the year your production is set, then work back to the models that were actually on the road then. Our [classic car model guides](/models) describe each car's era, character, and presence on camera, which makes it far easier to brief a director or art department. Browse by make and model in [search](/search) to see what's available near your location." },
      { h3: "Condition depends on the role" },
      { ul: [
        "Hero cars: immaculate, concours-condition examples for close-ups and key scenes.",
        "Background cars: tidy, period-correct cars that fill a street without stealing focus.",
        "Character cars: patina and wear can be exactly right for a lived-in look.",
      ] },
      { h3: "Plan the shoot day with the owner" },
      { p: "Classics on set need a little more care than a modern car. Talk the owner through the day early: how long the car will be on location, whether it needs to be driven or only positioned, and who handles it. Most owners are happy to act as the car's minder, and often know its quirks better than anyone." },
      { ul: [
        "Agree call times, location, and how long the car is needed.",
        "Confirm whether the car is driven on camera or static.",
        "Discuss any styling (ribbons, props, or set dressing) before the day, as some materials mark delicate paint.",
        "Build in buffer time; classics prefer an unhurried schedule.",
      ] },
      { h3: "The booking itself" },
      { p: "Send a request with the dates and a clear note on what the shoot involves; owners respond best to specifics. Many film and photo bookings are effectively chauffeured, with the owner present; see [self-drive vs chauffeured](/guides/self-drive-vs-chauffeured) if you need the car driven by your own team instead." },
      { strong: "Find a period-accurate picture car for your production and send a detailed request." },
    ],
    faqs: [
      { q: "Can I hire a classic car for a film or photoshoot?", a: "Yes. Many owners are happy to provide their car as a picture car. Send a request with your dates and a clear note on what the shoot involves." },
      { q: "How do I find a period-accurate car?", a: "Work from the year your production is set, then use the model guides and search to find cars from that era available near your location." },
      { q: "Will the owner come with the car?", a: "Often, yes. Many shoot bookings are effectively chauffeured, with the owner present to handle and mind the car on set." },
    ],
    commercialLinks: [
      { label: "Search classic cars by make and model", href: "/search" },
      { label: "Browse classic model guides", href: "/models" },
      { label: "See occasion hire guides", href: "/hire" },
    ],
    related: ["best-classic-cars-for-special-occasions", "self-drive-vs-chauffeured"],
    publishedAt: "2026-04-16",
    updatedAt: "2026-06-01",
  },
  {
    slug: "summer-classic-car-hire-guide",
    category: "occasions",
    title: "Summer Classic Car Hire: Convertibles, Festivals and Sunny-Day Drives",
    excerpt:
      "The season made for an open top: how to plan a summer classic hire, from convertibles and festivals to coast roads and warm-evening cruises.",
    metaTitle: "Summer Classic Car Hire: Convertibles & Sunny Drives | {brandName}",
    metaDescription:
      "Plan the perfect summer classic car hire: choosing a convertible, booking ahead for peak season, festival and coast-road ideas, and tips for warm-weather driving.",
    hero: {
      image: "https://images.unsplash.com/photo-1502136969935-8d8eef54d77b?w=1920&q=80",
      alt: "Couple beside an open-top classic car in summer sunshine",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "Summer is what classic convertibles were built for. Roof down, a warm evening, a road that follows the coast; it's the kind of day a modern car simply can't deliver. If you're planning one, a little forward thinking makes all the difference." },
      { h3: "Book early: summer is peak season" },
      { p: "Convertibles and the best-loved roadsters get snapped up fast once the weather turns. Wedding season overlaps with the holidays, so the most desirable cars book months ahead. Check what's free for your dates in [search](/search) and request early." },
      { h3: "Choosing a summer car" },
      { ul: [
        "Open-top roadsters for the full wind-in-your-hair experience.",
        "Convertible grand tourers for longer, comfortable summer touring.",
        "Characterful small classics for relaxed coast and country lanes.",
      ] },
      { p: "Our [classic car model guides](/models) describe how each car behaves on a warm-weather drive, including which suit a long touring day and which are happiest on short, scenic runs." },
      { h3: "Ideas for the day" },
      { ul: [
        "A coast road with the roof down; see our [European road-trip routes](/guides/classic-car-road-trips-europe).",
        "A run to a summer festival, show, or country event.",
        "A warm-evening cruise to dinner with the top folded away.",
        "A milestone surprise; see [best classic cars for special occasions](/guides/best-classic-cars-for-special-occasions).",
      ] },
      { h3: "Warm-weather practicalities" },
      { p: "Classics and heat need a little respect. Plan an unhurried route with the odd stop, keep an eye on temperatures on a long climb, and pack sun protection; an open top offers no shade. Confirm the mileage allowance with the owner if you're covering real distance." },
      { strong: "Find a summer-ready convertible for your dates and make the most of the season." },
    ],
    faqs: [
      { q: "When should I book a classic convertible for summer?", a: "As early as you can. Summer is peak season and the best convertibles book months ahead, especially around weddings and the holidays." },
      { q: "What's the best classic car for a summer drive?", a: "An open-top roadster for short scenic runs, or a convertible grand tourer for comfortable longer touring. The model guides describe each car's strengths." },
      { q: "Are classic cars OK to drive in hot weather?", a: "Yes, with a little care. Plan an unhurried route, allow cooling stops on long climbs, and confirm the mileage allowance with the owner for longer trips." },
    ],
    commercialLinks: [
      { label: "Search classic convertibles", href: "/search" },
      { label: "Browse summer-ready models", href: "/models" },
      { label: "See road-trip routes", href: "/guides/classic-car-road-trips-europe" },
    ],
    related: ["classic-car-road-trips-europe", "best-classic-cars-for-special-occasions"],
    publishedAt: "2026-04-23",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-car-hire-in-london",
    category: "destinations",
    title: "Classic Car Hire in London: A Local Guide",
    excerpt:
      "Hiring a classic in and around London: what it's good for, the practicalities of city driving, and the best escapes within easy reach.",
    metaTitle: "Classic Car Hire in London: Local Guide | {brandName}",
    metaDescription:
      "A local guide to classic car hire in London: weddings and events in the city, driving and parking practicalities, and the best classic-friendly escapes nearby.",
    hero: {
      image: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=80",
      alt: "Classic car parked on an elegant London street",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "London is one of the busiest places in the country for classic car hire, and one of the most rewarding, as long as you play to the city's strengths. Whether you want a wedding car in town or a classic to escape it, here's how to approach it." },
      { h3: "What London hire is best for" },
      { ul: [
        "Weddings and events: chauffeured classics arriving at a city venue in style.",
        "Photoshoots and film: period cars against an instantly recognisable backdrop.",
        "Escapes: collecting a classic and heading straight for open country.",
      ] },
      { p: "For weddings specifically, our [classic wedding car guide](/guides/classic-wedding-car-hire-guide) covers timing and etiquette; for shoots, see [hiring classic cars for film and photoshoots](/guides/classic-cars-for-film-and-photoshoots)." },
      { h3: "City-driving practicalities" },
      { p: "Driving an unfamiliar classic in central London traffic is hard work, so most in-city hires are chauffeured; you get the entrance without grinding a vintage gearbox in queues. If you do want to self-drive, plan to head out of the centre quickly and check charging zones and parking with the owner. Our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) helps you decide." },
      { h3: "The best escapes within reach" },
      { p: "The real joy of a London hire is often leaving it behind. Within a couple of hours you can be on proper driving roads in the countryside; see our [UK road-trip guide](/guides/classic-car-road-trips-uk) for routes worth pointing the bonnet at." },
      { h3: "Booking in the capital" },
      { p: "Demand is high, so book early, particularly in wedding and summer season. Browse what's listed near you in [search](/search), and explore more places in our [city and region guides](/cities)." },
      { strong: "Find a classic car in London for your dates and book ahead of the rush." },
    ],
    faqs: [
      { q: "Can I hire a classic car in London?", a: "Yes. London has one of the largest selections of classic cars for hire. Most in-city bookings are chauffeured for weddings, events, and shoots." },
      { q: "Is it hard to drive a classic in central London?", a: "Yes, city traffic is demanding in an unfamiliar classic, which is why most in-city hires are chauffeured. Self-drivers usually head out of the centre quickly." },
      { q: "What can I do with a classic car hire near London?", a: "Plenty. Within a couple of hours you can reach proper driving roads in the countryside. See our UK road-trip guide for routes." },
    ],
    commercialLinks: [
      { label: "Search classic cars near London", href: "/search" },
      { label: "Browse city & region guides", href: "/cities" },
      { label: "See UK road-trip routes", href: "/guides/classic-car-road-trips-uk" },
    ],
    related: ["classic-car-road-trips-uk", "classic-wedding-car-hire-guide", "classic-cars-for-film-and-photoshoots"],
    publishedAt: "2026-05-07",
    updatedAt: "2026-06-01",
  },
  {
    slug: "classic-car-road-trips-uk",
    category: "destinations",
    title: "Classic Car Road Trips: Britain's Greatest Driving Routes",
    excerpt:
      "From the Cotswolds to the Highlands: the British roads made for a classic, and how to plan an unforgettable home-soil road trip.",
    metaTitle: "Best UK Classic Car Road Trips & Driving Routes | {brandName}",
    metaDescription:
      "Britain's greatest driving roads for a classic car road trip, including the Cotswolds, Snowdonia, the Lake District and the Highlands, with planning tips and the cars to do them in.",
    hero: {
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80",
      alt: "Classic car on a winding British country road",
    },
    author: "The {brandName} Team",
    readingMinutes: 8,
    body: [
      { p: "You don't need to cross the Channel to find roads worth a classic. Britain is laced with driving routes that feel made for an open top and a willing engine, and a home-soil trip skips the cross-border paperwork entirely. Here are the routes to build a trip around." },
      { h3: "The routes worth planning a trip around" },
      { ul: [
        "The Cotswolds: honey-stone villages and gentle ridge roads at an unhurried pace.",
        "Snowdonia and mid-Wales: dramatic passes and empty, sweeping bends.",
        "The Lake District: fells, lakes, and roads that reward a careful, characterful classic.",
        "The Scottish Highlands and the NC500: big, open, unforgettable touring country.",
      ] },
      { h3: "Choosing the right car for British roads" },
      { p: "Match the car to the terrain. A nimble roadster is pure joy on tight country lanes; a comfortable grand tourer makes light of a long Highlands day. Our [classic car model guides](/models) describe how each car copes with real-world British driving, weather included." },
      { p: "These are self-drive trips by nature; the driving is the point. If it's your first classic, read [how to hire a classic car](/guides/how-to-hire-a-classic-car) first, and [what you need to hire a classic car](/guides/what-you-need-to-hire-a-classic-car) for the checklist." },
      { h3: "Planning a British road trip" },
      { ul: [
        "Confirm a generous mileage allowance; touring routes add up fast.",
        "Pack for changeable weather, especially with an open top.",
        "Build in slack: classics like a relaxed schedule and the odd stop.",
        "Base near your starting route and pick up the car locally.",
      ] },
      { p: "Many of these routes are within easy reach of a city pickup; see our [London hire guide](/guides/classic-car-hire-in-london) and browse classic-friendly places in our [city and region guides](/cities)." },
      { strong: "Pick a route, find a car near the start, and plan a great British drive." },
    ],
    faqs: [
      { q: "What's the best classic car road trip in the UK?", a: "It depends on the driving you want: the Cotswolds for gentle scenery, Snowdonia and the Highlands for dramatic passes. All reward an unhurried classic." },
      { q: "What kind of classic suits British roads?", a: "A light roadster for tight country lanes, or a comfortable grand tourer for long touring days. The model guides describe each car's road manners." },
      { q: "How do mileage limits work on a road trip?", a: "Many listings include a daily allowance with a per-mile charge beyond it. For a touring route, confirm a generous allowance with the owner in advance." },
    ],
    commercialLinks: [
      { label: "Find a car near your route", href: "/search" },
      { label: "Browse city & region guides", href: "/cities" },
      { label: "Explore touring-ready models", href: "/models" },
    ],
    related: ["classic-car-road-trips-europe", "classic-car-hire-in-london", "how-to-hire-a-classic-car"],
    publishedAt: "2026-05-14",
    updatedAt: "2026-06-01",
  },
  {
    slug: "jaguar-e-type-hire-guide",
    category: "model-guides",
    title: "Jaguar E-Type Hire Guide: The Most Beautiful Car Ever Made",
    excerpt:
      "What it's really like to hire and drive a Jaguar E-Type: the experience, the occasions it suits, and what to know before you book one.",
    metaTitle: "Jaguar E-Type Hire Guide: What to Know | {brandName}",
    metaDescription:
      "A hire guide to the Jaguar E-Type: what it's like to drive, the best occasions to hire one, self-drive vs chauffeured, and what to check before you book.",
    hero: {
      image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80",
      alt: "Classic Jaguar sports car on an open road",
    },
    author: "The {brandName} Team",
    readingMinutes: 7,
    body: [
      { p: "Enzo Ferrari is said to have called it the most beautiful car ever made, and few would argue. The Jaguar E-Type is the classic everyone recognises for its long bonnet, perfect proportions, and a presence that turns heads six decades on. Hiring one is a genuine bucket-list experience." },
      { h3: "What it's like to drive" },
      { p: "An E-Type is fast, low, and characterful, a true 1960s grand tourer rather than a modern sports car. The straight-six pulls beautifully, the cabin is snug, and the experience is all about the occasion rather than outright speed. It rewards a relaxed, confident driver who'll savour it." },
      { h3: "Best occasions for an E-Type" },
      { ul: [
        "Weddings: few cars photograph as well; see our [wedding car guide](/guides/classic-wedding-car-hire-guide).",
        "Milestone gifts and anniversaries: see [best classic cars for special occasions](/guides/best-classic-cars-for-special-occasions).",
        "A self-drive experience day on a great road.",
        "Film, TV and photoshoots: an instant period icon.",
      ] },
      { h3: "Self-drive or chauffeured?" },
      { p: "Both are offered. For weddings and events most people choose chauffeured; you get the entrance without managing an unfamiliar gearbox. For an experience day, self-drive is the whole point. Our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) weighs it up, and an E-Type self-drive usually carries firmer age and experience terms given the car's value." },
      { h3: "What to check before you book" },
      { ul: [
        "Whether the car is a roadster (open) or fixed-head coupé.",
        "Series and era, if period accuracy matters for your occasion.",
        "Mileage allowance for a self-drive day.",
        "Age, licence, and experience requirements for self-drive.",
      ] },
      { p: "See the wider [model guides](/models) for how the E-Type compares to other icons, then find one for your dates in [search](/search)." },
      { strong: "Find a Jaguar E-Type for hire and tick off a true bucket-list classic." },
    ],
    faqs: [
      { q: "Can I hire a Jaguar E-Type?", a: "Yes. E-Types are among the most sought-after classics for hire, offered both self-drive and chauffeured. Book early, as the best examples go fast." },
      { q: "Is an E-Type available to self-drive?", a: "Often, yes, though owners usually set firmer age and experience terms given the car's value. For weddings and events, chauffeured is the more common choice." },
      { q: "What's an E-Type like to drive?", a: "It's a characterful 1960s grand tourer: fast, low, and all about the occasion. It rewards a relaxed, confident driver who'll savour the experience." },
    ],
    commercialLinks: [
      { label: "Search for a Jaguar E-Type", href: "/search" },
      { label: "Browse all classic model guides", href: "/models" },
      { label: "See wedding car guides", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    related: ["porsche-911-classic-hire-guide", "classic-wedding-car-hire-guide", "best-classic-cars-for-special-occasions"],
    publishedAt: "2026-05-21",
    updatedAt: "2026-06-01",
  },
  {
    slug: "porsche-911-classic-hire-guide",
    category: "model-guides",
    title: "Classic Porsche 911 Hire Guide: The Everyday Icon",
    excerpt:
      "Hiring an air-cooled Porsche 911: what makes it special, the occasions it suits, and what to know before you take the keys.",
    metaTitle: "Classic Porsche 911 Hire Guide: What to Know | {brandName}",
    metaDescription:
      "A hire guide to the classic air-cooled Porsche 911: what it's like to drive, the best occasions, self-drive vs chauffeured, and what to check before you book.",
    hero: {
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80",
      alt: "Detail of a classic Porsche sports car",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "The air-cooled Porsche 911 is the rare classic that's as happy on a long drive as it is parked outside a venue. That distinctive shape barely changed for decades, and the flat-six soundtrack is unmistakable. It's the enthusiast's choice for a driving day." },
      { h3: "What it's like to drive" },
      { p: "A classic 911 is engaging, communicative, and genuinely usable, firmer and more focused than a grand tourer but far more livable than its reputation suggests. The rear-engined balance gives it a character all its own, and the flat-six is the highlight. It's a car best enjoyed self-drive on a great road." },
      { h3: "Best occasions for a classic 911" },
      { ul: [
        "Self-drive experience days: its natural habitat.",
        "Road trips on proper driving roads: see our [UK](/guides/classic-car-road-trips-uk) and [European](/guides/classic-car-road-trips-europe) route guides.",
        "Milestone birthday gifts for an enthusiast.",
        "Photoshoots wanting a sharp, era-defining shape.",
      ] },
      { h3: "Self-drive or chauffeured?" },
      { p: "The 911 is overwhelmingly a self-drive car; the driving is the appeal. Owners typically set sensible age and experience terms. If you'd rather be driven for an event, chauffeured options exist; our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) helps you choose." },
      { h3: "What to check before you book" },
      { ul: [
        "Era and model: early cars feel very different to later air-cooled ones.",
        "Coupé, Targa, or cabriolet body style.",
        "Mileage allowance for a touring day.",
        "Age, licence, and experience requirements.",
      ] },
      { p: "Compare it against the other icons in our [model guides](/models), then find one for your dates in [search](/search)." },
      { strong: "Find a classic Porsche 911 for hire and point it at a great road." },
    ],
    faqs: [
      { q: "Can I hire a classic Porsche 911?", a: "Yes. Air-cooled 911s are popular hire cars, mostly offered self-drive for driving days and road trips. Owners set sensible age and experience terms." },
      { q: "Is a classic 911 hard to drive?", a: "Less than its reputation suggests. It's engaging and focused but genuinely usable, with a distinctive rear-engined character and a wonderful flat-six." },
      { q: "What's the best use for a classic 911 hire?", a: "A self-drive experience day or a road trip on proper driving roads; that's exactly where the car shines." },
    ],
    commercialLinks: [
      { label: "Search for a classic Porsche 911", href: "/search" },
      { label: "Browse all classic model guides", href: "/models" },
      { label: "See road-trip routes", href: "/guides/classic-car-road-trips-uk" },
    ],
    related: ["jaguar-e-type-hire-guide", "vw-classic-camper-hire-guide", "classic-car-road-trips-uk"],
    publishedAt: "2026-05-28",
    updatedAt: "2026-06-01",
  },
  {
    slug: "vw-classic-camper-hire-guide",
    category: "model-guides",
    title: "Classic VW Camper Hire Guide: The Original Adventure",
    excerpt:
      "Hiring a classic VW camper van: the laid-back appeal, the trips it's made for, and the practical things to know before you set off.",
    metaTitle: "Classic VW Camper Van Hire Guide: What to Know | {brandName}",
    metaDescription:
      "A hire guide to the classic VW camper van: its laid-back appeal, the best trips and occasions, what to expect on the road, and what to check before you book.",
    hero: {
      image: "https://images.unsplash.com/photo-1502136969935-8d8eef54d77b?w=1920&q=80",
      alt: "Classic VW camper van parked by the coast",
    },
    author: "The {brandName} Team",
    readingMinutes: 6,
    body: [
      { p: "Nothing says relaxed adventure quite like a classic VW camper. It's less about speed and more about the journey: pottering to the coast, parking up with a view, and slowing the whole day down. For weekends away and feel-good occasions, few classics are more fun." },
      { h3: "What it's like to drive" },
      { p: "A classic camper is leisurely by design. You sit high, you take it gently, and you arrive in a good mood. It's the antidote to a fast car; the point is the pace, not the pace. Plan a relaxed route and let it set the rhythm." },
      { h3: "Best occasions for a VW camper" },
      { ul: [
        "Coast and countryside weekends away.",
        "Festivals, shows, and laid-back summer events; see our [summer hire guide](/guides/summer-classic-car-hire-guide).",
        "Quirky wedding transport and photo backdrops.",
        "A feel-good gift or experience day for someone who loves the open road.",
      ] },
      { h3: "Self-drive or chauffeured?" },
      { p: "Campers are usually self-drive; half the joy is pottering along yourself. Owners set straightforward age and experience terms. For events where you'd rather be driven, some are offered chauffeured; our [self-drive vs chauffeured guide](/guides/self-drive-vs-chauffeured) covers the choice." },
      { h3: "What to check before you book" },
      { ul: [
        "Sleeping and seating capacity if you're staying overnight.",
        "Whether it's set up for camping or styled for events only.",
        "Mileage allowance: campers cover ground slowly, so plan realistic distances.",
        "Age, licence, and experience requirements.",
      ] },
      { p: "See how the camper compares to the sportier icons in our [model guides](/models), then find one for your dates in [search](/search)." },
      { strong: "Find a classic VW camper for hire and plan a slow, happy adventure." },
    ],
    faqs: [
      { q: "Can I hire a classic VW camper van?", a: "Yes. Classic campers are popular hire vehicles for weekends away, festivals, and events, usually offered self-drive with straightforward terms." },
      { q: "What's a classic VW camper like to drive?", a: "Leisurely by design. You sit high and take it gently; the appeal is the relaxed pace, so plan an unhurried route and enjoy the journey." },
      { q: "Can I sleep in a hired VW camper?", a: "Some are set up for camping and some are styled for events only. Check the sleeping and seating capacity on the listing before you book." },
    ],
    commercialLinks: [
      { label: "Search for a classic VW camper", href: "/search" },
      { label: "Browse all classic model guides", href: "/models" },
      { label: "See the summer hire guide", href: "/guides/summer-classic-car-hire-guide" },
    ],
    related: ["porsche-911-classic-hire-guide", "summer-classic-car-hire-guide", "classic-car-road-trips-uk"],
    publishedAt: "2026-06-04",
    updatedAt: "2026-06-04",
  },
];
